import { useEffect, useRef } from "react";
import { useCookieState } from "cookie-state";
import { setTheme, useTheme, type Theme } from "campaign-builder/theme";
import { sharedCookieDomain } from "campaign-builder/sharedCookieDomain";

/**
 * Bridges the builder's theme store to the shared cross-app theme cookie via
 * `cookie-state` (PAD-31716). platform-next and this SPA run on separate
 * origins, so the cookie — not localStorage — keeps dark mode in sync.
 *
 * - On mount: adopt the shared cookie if it differs from the active theme.
 * - On every change: mirror the active theme back into the cookie.
 *
 * The pre-paint script in public/index.html seeds the `dark` class from the
 * same cookie before first paint, so there's no flash. Renders nothing.
 */
export const ThemeCookieBridge = () => {
  const active = useTheme();
  const { value: cookieTheme, setValue: setCookieTheme } = useCookieState<Theme>("theme", "light", {
    domain: sharedCookieDomain(),
    sameSite: "lax",
    days: 365,
  });
  const adopted = useRef(false);

  useEffect(() => {
    if (adopted.current) return;
    adopted.current = true;
    if ((cookieTheme === "light" || cookieTheme === "dark") && cookieTheme !== active) {
      setTheme(cookieTheme);
    }
    // Adopt once, on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((active === "light" || active === "dark") && active !== cookieTheme) {
      setCookieTheme(() => active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return null;
};
